import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo } from 'react';
import {
  useBaseOwnerId,
  useHasRoleOf,
  useModel
} from '@rhino-project/core/hooks';
import { useMutation } from '@tanstack/react-query';
import { networkApiCall } from '@rhino-project/core/lib';
import { toast } from '@heroui/react';
import { useNonArchivedSeasons } from './ots';
import { useRosterStatuses } from './rosterstatus';

export const useExternalPlayerAdd = (externalPlayerId?: string | number) => {
  const { t } = useTranslation();

  const baseOwnerId = useBaseOwnerId();
  const isAdmin = useHasRoleOf('admin');
  const playerModel = useModel('player');

  const { resources: seasons } = useNonArchivedSeasons();
  const { resources: planningStatuses } = useRosterStatuses({
    status_type: 'planning'
  });
  const { resources: activeStatuses } = useRosterStatuses({
    status_type: 'active'
  });

  const { mutate } = useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      const endpoint = `${playerModel.path}/external_player_add`;
      return networkApiCall(endpoint, { method: 'post', data });
    },
    onSuccess: (response) => {
      const player = (response as { data?: { display_name?: string } })?.data;
      toast.success(
        t('frontend.messages.player_added', {
          name: player?.display_name ?? t('frontend.messages.player')
        })
      );
    },
    onError: () => toast.danger(t('frontend.messages.unable_to_add_player'))
  });

  const handleAdd = useCallback(
    (
      team_season: string | number,
      ePlayerId: string | number | undefined = externalPlayerId
    ) => {
      mutate({
        organization: baseOwnerId,
        external_player: ePlayerId,
        team_season
      });
    },
    [baseOwnerId, externalPlayerId, mutate]
  );

  const memberActions = useMemo(() => {
    if (
      !isAdmin ||
      !seasons?.results?.[0] ||
      !planningStatuses?.results?.[0] ||
      !activeStatuses?.results?.[0]
    )
      return null;

    const actions: {
      name: number | string;
      label: string;
      onAction: typeof handleAdd;
    }[] = seasons.results.map((ts) => ({
      name: ts.id!,
      label: t('frontend.messages.add_season_status', {
        season: ts.season.name,
        status:
          ts.status === 'active'
            ? activeStatuses.results[0].name
            : planningStatuses.results[0].name
      }),
      onAction: handleAdd
    }));

    actions.push({
      name: '',
      label: t('frontend.messages.add_to_players_only'),
      onAction: handleAdd
    });

    return actions;
  }, [isAdmin, seasons, planningStatuses, activeStatuses, t, handleAdd]);

  return { memberActions, handleAdd };
};
